// Shared fan listening-audio preferences (ADR 0064 / LSC-544).
//
// The fan's OWN volume + mute is a single listening preference that must behave
// IDENTICALLY on every watch surface — the live Fan watch (ConsumerWatch) AND
// the on-demand replay/VOD player (RecordingsOnDemand). Both compose the same
// `@legends/ui` VolumeControl and apply the value to their media element(s).
// Keeping the keys + load/save + the iOS detection here (one source of truth)
// is why the custom replay control is consistent with live and actually sticks
// across a layout swap / live↔replay (LSC-544 fixed the replay player, which had
// native controls only and no working custom control).
//
// Session-persisted (sessionStorage) like the original ConsumerWatch prefs, and
// every access is try/catch-wrapped so a private/blocked store never throws
// (jsdom test env has no sessionStorage either). This is the listener's own
// volume — orthogonal to the autoplay-policy "Enable audio" gesture (ADR 0035)
// and any operator production/hold mute (ADR 0053/0055).

export const VOLUME_KEY = "legends.watch.volume";
export const MUTED_KEY = "legends.watch.muted";

// iOS / iPadOS ignore programmatic HTMLMediaElement.volume (volume is controlled
// by the hardware buttons), so the volume SLIDER is inert there and only misleads
// — mute still works. Detect it once so the fan renders a mute-only VolumeControl
// on iOS (ADR 0076). iPadOS 13+ reports a desktop "Macintosh" UA, so also treat a
// touch-capable Mac as iOS. Guarded for non-browser (test/SSR) environments.
export const IS_IOS = (() => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const touchMac =
    /Macintosh/.test(ua) && typeof navigator.maxTouchPoints === "number" && navigator.maxTouchPoints > 1;
  return /iPad|iPhone|iPod/.test(ua) || touchMac;
})();

/** The fan's last listening volume, 0..1 (defaults to full). */
export function loadWatchVolume(): number {
  try {
    const raw = sessionStorage.getItem(VOLUME_KEY);
    if (raw == null) return 1;
    const n = Number(raw);
    return Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : 1;
  } catch {
    return 1;
  }
}

/** Whether the fan muted their own audio (defaults to unmuted). */
export function loadWatchMuted(): boolean {
  try {
    return sessionStorage.getItem(MUTED_KEY) === "1";
  } catch {
    return false;
  }
}

/** Persist the fan's volume; a blocked store keeps the in-memory value. */
export function saveWatchVolume(v: number): void {
  try {
    sessionStorage.setItem(VOLUME_KEY, String(Math.min(1, Math.max(0, v))));
  } catch {
    // sessionStorage unavailable — keep the in-memory value
  }
}

/** Persist the fan's mute; a blocked store keeps the in-memory value. */
export function saveWatchMuted(m: boolean): void {
  try {
    sessionStorage.setItem(MUTED_KEY, m ? "1" : "0");
  } catch {
    // sessionStorage unavailable — keep the in-memory value
  }
}
